import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ShortcutEntry {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: ShortcutEntry[];
}

const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    title: 'Global',
    shortcuts: [
      { keys: ['Ctrl', 'K'],   description: 'Open Omnisearch' },
      { keys: ['?'],            description: 'Show keyboard shortcuts' },
      { keys: ['Esc'],          description: 'Close modal / dismiss overlay' },
      { keys: ['Alt', 'A'],     description: 'Open accessibility settings' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Alt', '1'], description: 'Go to Dashboard' },
      { keys: ['Alt', '2'], description: 'Go to Tasks' },
      { keys: ['Alt', '3'], description: 'Go to Calendar' },
      { keys: ['Alt', '4'], description: 'Go to Groceries' },
      { keys: ['Alt', '5'], description: 'Go to Notifications' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['Alt', 'N'], description: 'Create new item (context-aware)' },
    ],
  },
];

interface Props {
  onClose: () => void;
}

export function KeyboardShortcutsModal({ onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard Shortcuts"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--theme-card)', border: '1px solid var(--theme-border)' }}
      >
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
              Keyboard Shortcuts
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-muted)' }}>Press ? to toggle this panel</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close keyboard shortcuts"
          >
            <X size={18} style={{ color: 'var(--theme-text-muted)' }} />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-5">
          {SHORTCUT_SECTIONS.map(section => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--theme-text-muted)' }}>
                {section.title}
              </h3>
              <div className="space-y-1.5">
                {section.shortcuts.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-sm" style={{ color: 'var(--theme-text)' }}>{s.description}</span>
                    <div className="flex items-center gap-1">
                      {s.keys.map((key, ki) => (
                        <React.Fragment key={ki}>
                          {ki > 0 && <span className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>+</span>}
                          <kbd
                            className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold"
                            style={{
                              backgroundColor: 'var(--theme-bg-subtle)',
                              border: '1px solid var(--theme-border)',
                              color: 'var(--theme-text)',
                            }}
                          >
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
